import React, { useMemo } from 'react';
import Dialog from '../../../ui/Dialog/Dialog';
import DataTable from '../../../ui/DataTable/DataTable';
import { CellText, CellId, CellPlate, CellNumber, CellStatus } from '../../../ui/DataTable/cells';
import Button from '../../../ui/Button';
import { ICONS } from '../../../ui/icon-map';
import { utils, writeFile } from 'xlsx';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatContractDateTime } from '../../../utils/contractDateUtils';
import { contractExcelDate, contractDateSheetCell, fitContractDateColumns } from '../../../utils/excelDates';

// Internal fields (times in ms used for the maths) are searchable but not shown
// (`version` / `vehicle_id`: the store's own keys on a car or a log - USE_CONTRACTS_V2 - never a column)
const HIDDEN = new Set(['__source', 'id', 'pickupAt', 'dropoffAt', 'createdByRaw', 'version', 'vehicle_id']);

// A contract's pick-up / drop-off field (pickupDate, 'Pickup Date', 'Drop-off Date', ...): shown dd/mm/yyyy HH:MM,
// sorted by its real time and exported as a real Excel date (owner, 27 Sep 2026)
const CONTRACT_DATE_FIELD = /^(pick-?up|drop-?off)\s*date$/i;
const shownContractDate = (val) => formatContractDateTime(val) || val;

const humanize = (col) => col.charAt(0).toUpperCase() + col.slice(1).replace(/([A-Z])/g, ' $1');

// Pick the cell type from the field name (the records come from several sources).
const typedCell = (col, val) => {
    if (/plate/i.test(col)) return <CellPlate>{val}</CellPlate>;
    if (/contract|booking|invoice|ticket|ref/i.test(col)) return <CellId>{val}</CellId>;
    if (/status/i.test(col)) return <CellStatus status={val} />;
    if (/date|time/i.test(col)) return <span className="ds-cell-date">{val}</span>;
    return <CellText>{val}</CellText>;
};

// ``loading`` = the records are still being read; ``action`` = {label, onClick}: the page the records belong to (the
// Overview's figures - owner, 3 Oct 2026)
// ``exportKey``: the permission its Excel asks (permissions phase 4, 4 Oct 2026: the KPI pages' "every KPI export");
// null = the page's own (the Overview's figures, also on the Dashboard)
const DrillDownModal = ({ isOpen, onClose, title, data = [], loading = false, action = null, note = '', exportKey = 'export_kpi' }) => {
    const rows = useMemo(() => (Array.isArray(data) ? data.filter(Boolean) : []), [data]);
    const { hasPermission } = usePermissions();
    const canExport = !exportKey || hasPermission(exportKey);

    // Export exactly the (searched) records with every field, same file name as before
    const handleExport = (exportRows) => {
        const ws = fitContractDateColumns(utils.json_to_sheet(exportRows.map((row) => (row && typeof row === 'object'
            ? Object.fromEntries(Object.entries(row).map(([k, v]) => [k, CONTRACT_DATE_FIELD.test(k) && v ? contractDateSheetCell(v, v) : v]))
            : row))));
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Data');
        writeFile(wb, `${String(title || 'Export').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    // Determine columns dynamically from all items; search still covers every field
    const { keys, searchKeys } = useMemo(() => {
        const visible = new Set();
        const all = new Set();
        rows.forEach(item => {
            if (item && typeof item === 'object') {
                Object.keys(item).forEach(k => {
                    all.add(k);
                    if (!HIDDEN.has(k) && !k.startsWith('_')) {
                        visible.add(k);
                    }
                });
            }
        });
        return { keys: Array.from(visible), searchKeys: Array.from(all) };
    }, [rows]);

    const columns = useMemo(() => [
        { key: '__rowNumber', header: '#', sortable: false, searchable: false, exportable: false, hideable: false, width: 48, cell: (row, value, i) => <CellNumber value={i + 1} /> },
        ...keys.map(col => ({
            key: col,
            header: humanize(col),
            accessor: (row) => row[col],
            ...(CONTRACT_DATE_FIELD.test(col) ? { sortValue: (row) => contractExcelDate(row[col])?.serial ?? null } : {}),
            cell: (row) => {
                const val = row[col];
                // a real 0 is shown (a registration expiring today: 0 days) - only nothing is "—"
                if (val == null || val === '' || val === false) return <span className="ds-cell-empty">—</span>;
                // Other dates display as-is from the data source; a contract's pick-up / drop-off the one way
                return typedCell(col, CONTRACT_DATE_FIELD.test(col) ? shownContractDate(val) : val);
            },
        })),
    ], [keys]);

    return (
        <Dialog
            open={Boolean(isOpen)}
            onClose={onClose}
            title={title}
            description={loading ? 'Loading…' : `${rows.length} records${note ? ` · ${note}` : ''}`}
            size="xl"
            footer={(
                <>
                    {action && <Button variant="secondary" onClick={() => { onClose(); action.onClick(); }}>{action.label}</Button>}
                    <Button variant="secondary" onClick={onClose}>Close</Button>
                </>
            )}
        >
            {/* a contract number here opens the app's quick menu (ContractQuickActions) like anywhere else */}
            <div data-quick-actions="on">
            <DataTable
                columns={columns}
                data={rows}
                rowKey={(row, i) => i}
                searchKeys={searchKeys}
                searchPlaceholder="Search in these records..."
                onExport={canExport ? handleExport : undefined}
                emptyIcon={ICONS.operations}
                emptyTitle={loading ? 'Loading…' : 'No matching records found.'}
                maxHeight="60vh"
                card={false}
            />
            </div>
        </Dialog>
    );
};

export default DrillDownModal;
